import { useEffect, useState } from 'react';
import { doc, onSnapshot, collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';

const TOPIC_LABELS = {
  communication: 'Communication',
  hotel: 'Hotel',
  food: 'Food',
  activities: 'Activities',
  sessions: 'Training sessions',
  fixtures: 'Fixtures',
  transport: 'Transport',
  tourLeader: 'Tour leader',
  overall: 'Overall (recommend)',
};

function scoreColor(score) {
  if (score >= 9) return { bg: '#ECFDF5', text: '#065F46' };
  if (score >= 6) return { bg: '#FFF7ED', text: '#92400E' };
  return { bg: '#FEF2F2', text: '#991B1B' };
}

function buildWhatsAppMessage(request, surveyUrl) {
  const company = request.brand === 'your_tour' ? 'Your Tour' : request.brand === 'padelante' ? 'Padelante' : 'MST Sports Tours';
  const name = request.contactName ? `Hi ${request.contactName}, ` : 'Hi, ';
  return `${name}thanks for touring with ${company}! We'd love your feedback — it only takes 2 minutes: ${surveyUrl}`;
}

export default function SurveyPanel({ requestId, request }) {
  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [copyPending, setCopyPending] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'requests', requestId, 'survey', 'response'), (snap) => {
      setResponse(snap.exists() ? snap.data() : null);
      setLoading(false);
    });
    return unsub;
  }, [requestId]);

  // Once a "copy link" request generates a token, the parent's onSnapshot on
  // the request doc updates `request.surveyToken` — auto-copy as soon as it appears.
  // Clipboard writes require a direct user gesture — auto-copying from a useEffect
  // is always blocked by browsers. Instead, prompt the user to click once more;
  // the token is now cached so the second click copies immediately.
  useEffect(() => {
    if (copyPending && request.surveyToken) {
      setCopyPending(false);
      flash('Link ready — click "Copy WhatsApp link" to copy it.');
    }
  }, [copyPending, request.surveyToken]);

  function flash(message) {
    setFeedback(message);
    setTimeout(() => setFeedback(''), 4000);
  }

  async function copyLink(token) {
    const surveyUrl = `https://msttoursportal.co.uk/survey-respond?token=${token}`;
    try {
      await navigator.clipboard.writeText(buildWhatsAppMessage(request, surveyUrl));
      flash('Message + link copied — paste it into WhatsApp.');
    } catch (err) {
      console.error(err);
      setError('Could not copy to clipboard.');
    }
  }

  async function handleCopyLink() {
    setError('');
    if (request.surveyToken) {
      await copyLink(request.surveyToken);
      return;
    }
    setCopyPending(true);
    try {
      await addDoc(collection(db, 'surveyEmailRequests'), {
        requestId, mode: 'link_only', requestedAt: serverTimestamp(),
      });
    } catch (err) {
      console.error(err);
      setError('Failed to generate the link.');
      setCopyPending(false);
    }
  }

  async function handleSendEmail() {
    setError('');
    setSending(true);
    try {
      await addDoc(collection(db, 'surveyEmailRequests'), {
        requestId, mode: 'email', requestedAt: serverTimestamp(),
      });
      flash(response ? 'Survey re-sent by email.' : 'Survey sent by email.');
    } catch (err) {
      console.error(err);
      setError('Failed to send the survey email.');
    } finally {
      setSending(false);
    }
  }

  async function handleDelete() {
    if (!window.confirm(
      'Delete this survey response? This removes the stored feedback and breaks the existing link — use this after testing, then send a fresh survey to the real organiser.'
    )) return;
    setError('');
    setDeleting(true);
    try {
      await addDoc(collection(db, 'surveyEmailRequests'), {
        requestId, mode: 'delete', requestedAt: serverTimestamp(),
      });
      flash('Survey response deleted.');
    } catch (err) {
      console.error(err);
      setError('Failed to delete the survey response.');
    } finally {
      setDeleting(false);
    }
  }

  const isCompleted = response?.status === 'completed';

  return (
    <div className="bg-white rounded-[12px] border border-[#E5E7EB] p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px]">Post-Tour Survey</h3>
        <div className="flex items-center gap-2">
          <button
            onClick={handleCopyLink}
            disabled={copyPending}
            className="text-[12px] font-medium text-pb hover:text-navy border border-pb/20 hover:border-pb/40 px-3 py-1.5 rounded-[8px] transition disabled:opacity-50"
          >
            Copy WhatsApp link
          </button>
          <button
            onClick={handleSendEmail}
            disabled={sending}
            className="text-[12px] font-medium text-pb hover:text-navy border border-pb/20 hover:border-pb/40 px-3 py-1.5 rounded-[8px] transition disabled:opacity-50"
          >
            {response ? 'Resend email' : 'Send email'}
          </button>
          {response && (
            <button
              onClick={handleDelete}
              disabled={deleting}
              className="text-[12px] font-medium text-dr hover:text-[#7A1D14] border border-dr/20 hover:border-dr/40 px-3 py-1.5 rounded-[8px] transition disabled:opacity-50"
            >
              Delete feedback
            </button>
          )}
        </div>
      </div>

      {feedback && <p className="text-[12px] text-[#065F46] mb-3">{feedback}</p>}
      {error && <p className="text-[12px] text-dr mb-3">{error}</p>}

      {loading ? (
        <p className="text-[13px] text-[#9CA3AF]">Loading…</p>
      ) : !isCompleted ? (
        <p className="text-[13px] text-[#9CA3AF]">
          {response?.emailSentAt
            ? 'Email sent — awaiting the organiser\'s response.'
            : response
            ? 'Link generated (no email sent yet) — awaiting the organiser\'s response.'
            : 'Not sent yet.'}
        </p>
      ) : (
        <div className="space-y-3">
          <div className="flex flex-wrap gap-2">
            {Object.entries(TOPIC_LABELS).map(([key, label]) => {
              const score = response.scores?.[key];
              if (score === undefined) return null;
              const { bg, text } = scoreColor(score);
              return (
                <div key={key} className="rounded-[8px] px-3 py-2" style={{ backgroundColor: bg }}>
                  <p className="text-[10.5px] font-semibold uppercase tracking-[0.5px]" style={{ color: text }}>{label}</p>
                  <p className="text-[16px] font-bold" style={{ color: text }}>{score}/10</p>
                </div>
              );
            })}
          </div>
          {Object.entries(TOPIC_LABELS).some(([key]) => response.comments?.[key]) && (
            <div className="space-y-1.5 pt-2">
              {Object.entries(TOPIC_LABELS).map(([key, label]) => (
                response.comments?.[key] ? (
                  <p key={key} className="text-[13px] text-[#374151]"><span className="font-semibold">{label}:</span> {response.comments[key]}</p>
                ) : null
              ))}
            </div>
          )}
          {response.otherFeedback && (
            <p className="text-[13px] text-[#374151] pt-2 border-t border-[#F1F3F6]">
              <span className="font-semibold">Other feedback:</span> {response.otherFeedback}
            </p>
          )}
          {response.respondedAt?.seconds && (
            <p className="text-[11px] text-[#9CA3AF]">
              Responded {new Date(response.respondedAt.seconds * 1000).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
